/** Pure value helpers for TagConditionInput (kept apart for unit testing). */
import { isCanonicalSpec, type TagChip, type TagConditionValue, type TagSpec } from './types';

/** Wire shape of the whole condition — labels dropped, specs as stored. */
export interface WireTagCondition {
  match_mode: 'and' | 'or';
  specs: TagSpec[];
}

/** Stable chip identity for dedup: pinned chips by tag id; by-code chips by
 *  exact code + case-folded name (the backend matches names iexact).
 *
 *  The kind is part of the key. A `canonical` chip and a `tag_name` chip that
 *  spell the same word resolve to different tag sets, so neither may dedupe
 *  the other away — the backend's spec_chip_key() draws the same distinction. */
export function specKey(spec: TagSpec): string {
  if ('tag_id' in spec) return `tag:${spec.tag_id}`;
  if (isCanonicalSpec(spec)) {
    return `canon:${spec.category_code} ${spec.canonical.toLowerCase()}`;
  }
  return `code:${spec.category_code} ${spec.tag_name.toLowerCase()}`;
}

/** Strip display labels — the condition on the wire is {match_mode, specs}. */
export function toWireCondition(value: TagConditionValue): WireTagCondition {
  return { match_mode: value.matchMode, specs: value.chips.map((c) => c.spec) };
}

// ---------------------------------------------------------------------------
// Chip wire shapes. Every resource that owns a tag condition (notification
// feature flags, delivery blacklist rules) uses the SAME chip contract, so the
// row -> widget and widget -> payload conversions live here rather than in each
// feature module.
// ---------------------------------------------------------------------------

/** Read shape of one chip: a resolved concrete tag, or the stored spec strings. */
export type WireTagChipRead =
  | {
      tag: {
        id: number;
        name: string;
        is_deleted?: boolean;
        category: { id: number; code: string; name: string };
        tenant: { id: number; code: string; name: string } | null;
      };
    }
  | { category_code: string; tag_name: string }
  | { category_code: string; canonical: string };

/** Write shape of one chip (note `tag`, not `tag_id`). */
export type WireTagChipWrite =
  | { tag: number }
  | { category_code: string; tag_name: string }
  | { category_code: string; canonical: string };

/** Widget value from a row's `tags` + `tag_match_mode`. Labels come from the
 *  resolved read refs (pinned chips) or the stored strings (by-code chips). */
export function fromWireChips(
  chips: WireTagChipRead[] | null | undefined,
  matchMode: 'and' | 'or' | null | undefined,
): TagConditionValue {
  const mapped: TagChip[] = (chips ?? []).map((entry) => {
    if ('tag' in entry) {
      const t = entry.tag;
      return {
        spec: { tag_id: t.id },
        labels: {
          tenant: t.tenant ? t.tenant.code || t.tenant.name : null,
          category: t.category.name || t.category.code,
          tag: t.name,
        },
      };
    }
    if ('canonical' in entry) {
      return {
        spec: { category_code: entry.category_code, canonical: entry.canonical },
        // The `~` sigil is the spelling used everywhere else this shape appears
        // (rule args, {% has_tag %}, the alias grid), so the chip reads the way
        // the thing it configures is written.
        labels: { tenant: null, category: entry.category_code, tag: `~${entry.canonical}` },
      };
    }
    return {
      spec: { category_code: entry.category_code, tag_name: entry.tag_name },
      labels: { tenant: null, category: entry.category_code, tag: entry.tag_name },
    };
  });
  return { matchMode: matchMode ?? 'and', chips: mapped };
}

/** Widget chips → the write contract's chip shape (`{tag_id}` → `{tag: id}`). */
export function toWireChips(value: TagConditionValue): WireTagChipWrite[] {
  return value.chips.map((c) => {
    if ('tag_id' in c.spec) return { tag: c.spec.tag_id };
    if (isCanonicalSpec(c.spec)) {
      return { category_code: c.spec.category_code, canonical: c.spec.canonical };
    }
    return { category_code: c.spec.category_code, tag_name: c.spec.tag_name };
  });
}

/** Chip shape for hosts that cannot express a canonical — the notification
 *  `tag` block, whose reference resolves to ONE tag whose data gets injected.
 *
 *  Such a host also omits `fetchCanonicalsForCode`, so the builder never offers
 *  the path and this filter is belt-and-braces; it exists so the narrower write
 *  type is honoured at the type level rather than by convention. */
export type WireTagChipWriteNameOnly = Exclude<WireTagChipWrite, { canonical: string }>;

export function toWireChipsNameOnly(value: TagConditionValue): WireTagChipWriteNameOnly[] {
  return toWireChips(value).filter(
    (c): c is WireTagChipWriteNameOnly => !('canonical' in c),
  );
}

/** Compact per-chip labels for a grid cell / tooltip: "wan · VIP · gold". */
export function chipSummaryLabels(value: TagConditionValue): string[] {
  return value.chips.map((c) =>
    [c.labels.tenant, c.labels.category, c.labels.tag].filter((p) => p !== null).join(' · '),
  );
}
